/**
 * Shared article typography.
 *
 * Tailwind's `prose` covers headings and paragraphs but leaves tables, callouts,
 * code blocks and definition lists looking unstyled — which is exactly the
 * furniture real help-center articles are full of. These classes are applied
 * wherever article HTML is rendered (the editor's rich/preview modes and the
 * review card) so authored content looks the same everywhere.
 *
 * Written as an arbitrary-variant class string rather than a CSS file to stay
 * consistent with how the rest of this codebase styles things.
 */

/**
 * Element-level styles for rendered article HTML.
 *
 * Kept in one string so the editor and the preview can't drift apart. Uses
 * Tailwind's `[&_selector]:` arbitrary variants, so it composes with `prose`
 * and needs no global stylesheet or plugin.
 */
export const ARTICLE_CONTENT_CLASS = [
  // Base
  "prose prose-sm max-w-none dark:prose-invert",

  // Headings — tighter than prose's defaults, with a rule under h2 to separate
  // major sections the way published help centers usually do.
  "[&_h1]:text-2xl [&_h1]:font-semibold [&_h1]:mt-6 [&_h1]:mb-3",
  "[&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mt-6 [&_h2]:mb-2",
  "[&_h2]:border-b [&_h2]:border-border [&_h2]:pb-1.5",
  "[&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-1.5",
  "[&_h4]:text-sm [&_h4]:font-semibold [&_h4]:mt-3 [&_h4]:mb-1",

  // Body copy
  "[&_p]:my-2.5 [&_p]:leading-relaxed",
  "[&_a]:text-primary [&_a]:underline [&_a]:decoration-primary/40 [&_a]:underline-offset-2",
  "[&_a]:hover:decoration-primary",
  "[&_strong]:font-semibold",

  // Lists — restore the markers prose drops in some resets, and keep nesting readable.
  "[&_ul]:my-2.5 [&_ul]:pl-5 [&_ul]:list-disc",
  "[&_ol]:my-2.5 [&_ol]:pl-5 [&_ol]:list-decimal",
  "[&_li]:my-1 [&_li]:leading-relaxed",
  "[&_ul_ul]:my-1 [&_ul_ul]:list-[circle]",
  "[&_ol_ol]:my-1 [&_ol_ol]:list-[lower-alpha]",

  // Tables — the big gap. Full-width, zebra-striped, sticky-looking header,
  // and a horizontal scroll container so wide tables don't blow out the layout.
  "[&_table]:my-4 [&_table]:w-full [&_table]:border-collapse [&_table]:text-sm",
  "[&_table]:block [&_table]:overflow-x-auto",
  "[&_thead]:bg-muted",
  "[&_th]:border [&_th]:border-border [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_th]:align-top",
  "[&_td]:border [&_td]:border-border [&_td]:px-3 [&_td]:py-2 [&_td]:align-top",
  "[&_tbody_tr:nth-child(even)]:bg-muted/40",
  "[&_caption]:mt-2 [&_caption]:text-xs [&_caption]:text-muted-foreground [&_caption]:caption-bottom",

  // Blockquotes / callouts
  "[&_blockquote]:my-3 [&_blockquote]:border-l-4 [&_blockquote]:border-primary/40",
  "[&_blockquote]:bg-muted/40 [&_blockquote]:py-2 [&_blockquote]:pl-4 [&_blockquote]:pr-3",
  "[&_blockquote]:text-muted-foreground [&_blockquote]:not-italic",
  "[&_blockquote_p]:my-1",

  // Code
  "[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5",
  "[&_code]:font-mono [&_code]:text-[0.85em] [&_code]:before:content-none [&_code]:after:content-none",
  "[&_pre]:my-3 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:border [&_pre]:bg-muted",
  "[&_pre]:p-3 [&_pre]:text-xs [&_pre]:leading-relaxed",
  "[&_pre_code]:bg-transparent [&_pre_code]:p-0",

  // Definition lists — common in glossary-style articles, unstyled by default.
  "[&_dl]:my-3",
  "[&_dt]:mt-2 [&_dt]:font-semibold",
  "[&_dd]:ml-4 [&_dd]:text-muted-foreground",

  // Media
  // h-auto matters once a width is set: without it a resized image keeps its
  // intrinsic height and distorts. max-w-full still caps anything oversized.
  "[&_img]:my-3 [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-md [&_img]:border",
  "[&_figure]:my-3",
  "[&_figcaption]:mt-1 [&_figcaption]:text-xs [&_figcaption]:text-muted-foreground",

  // Rules
  "[&_hr]:my-5 [&_hr]:border-border",

  // Misc inline
  "[&_kbd]:rounded [&_kbd]:border [&_kbd]:bg-muted [&_kbd]:px-1.5 [&_kbd]:py-0.5 [&_kbd]:font-mono [&_kbd]:text-[0.8em]",
  "[&_mark]:rounded [&_mark]:bg-yellow-200 [&_mark]:px-0.5 dark:[&_mark]:bg-yellow-500/30",
  "[&_abbr]:cursor-help [&_abbr]:underline [&_abbr]:decoration-dotted",
].join(" ")

/**
 * HTML snippets the editor can insert, so authors get correct markup for the
 * elements above instead of hand-typing tags.
 */
export const ARTICLE_SNIPPETS: Array<{ label: string; description: string; html: string }> = [
  {
    label: "Table",
    description: "3-column table with a header row",
    html:
      "<table><thead><tr><th>Column</th><th>Column</th><th>Column</th></tr></thead>" +
      "<tbody><tr><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td></tr>" +
      "<tr><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td></tr></tbody></table><p></p>",
  },
  {
    label: "Callout",
    description: "Highlighted note block",
    html: "<blockquote><p><strong>Note:</strong> &nbsp;</p></blockquote><p></p>",
  },
  {
    label: "Steps",
    description: "Numbered procedure",
    html: "<ol><li>&nbsp;</li><li>&nbsp;</li><li>&nbsp;</li></ol><p></p>",
  },
  {
    label: "Definitions",
    description: "Term and description pairs",
    html: "<dl><dt>Term</dt><dd>Definition</dd><dt>Term</dt><dd>Definition</dd></dl><p></p>",
  },
  {
    label: "Code block",
    description: "Preformatted code",
    html: "<pre><code>&nbsp;</code></pre><p></p>",
  },
  {
    label: "Divider",
    description: "Horizontal rule",
    html: "<hr><p></p>",
  },
]
